<script setup>
import HospitalList from "@/components/hpo/dashboard/hospital/HospitalList";
import {useQuery} from "@vue/apollo-composable";
import {useLastResult} from "@/graphql/composable";
import {HpoHospitalList} from "@/graphql/queries/hpo/hpoHospitalList.graphql"
import {EmergencyPatientListQuery} from "@/graphql/queries/hpo/patients.graphql"
import {computed, onBeforeUnmount, onMounted, ref, useTemplateRef} from "vue";
import CountsBlock from "@/components/hpo/dashboard/CountsBlock";
import dayjs from "dayjs";
import {pgettext} from "@/utils/translate";
import EmergencyPatientList from "./patientListItems/EmergencyPatientList.vue";
import DashboardModalsContainer from "@/components/hpo/dashboard/DashboardModalsContainer.vue";
import QrScanButton from "../QrScanButton.vue";
import {useSearch} from "@/search";

const props = defineProps({
  hpo: {
    type: Object,
    required: true
  },
  member: {
    type: Object,
    required: true
  },
  patientCounts: {
    type: Object,
    required: true
  },
  patientCountsLoading: {
    type: Boolean,
    required: true
  },
  hpoPassword: {
    type: String,
    required: true
  },
  countries: {
    type: Array,
    required: true
  },
  mobileView: {
    type: Boolean,
    required: true,
  },
  chipProperties: {
    type: Object,
    required: true
  },
  personsTrackerTimestamp: {
    type: Object,
    required: true
  },
  hospitalsTrackerTimestamp: {
    type: Object,
    required: false
  }
})

const now = ref(new dayjs())
const modals = useTemplateRef('modals')


// Persons Query
const personsQueryVariables = computed(() => ({
  updateTimestamp: props.personsTrackerTimestamp,
  hpoId: props.hpo.id,
  hospitalIds: props.member.hospitals
}));
const {
  result: patientListResult,
  loading: patientListLoading
} = useQuery(EmergencyPatientListQuery, personsQueryVariables)
const patientList = useLastResult(patientListResult, [], data => data.erPatientList)

const {query, searchResultsOrAllItems: filteredPatientList} = useSearch(
  patientList, ['firstName', 'lastName', 'birthYear', 'birthNumber', 'note'], ['tic', 'temporaryIdentifier']
);

// Hospitals Query
const hospitalsQueryVariables = computed(() => ({
  updateTimestamp: props.hospitalsTrackerTimestamp,
  hpoId: props.hpo.id
}));
const {
  result: hospitalsResult,
  loading: hospitalListLoading
} = useQuery(HpoHospitalList, hospitalsQueryVariables)
const hospitalList = useLastResult(hospitalsResult, [], data => data.hpoHospitalList)

const anyLoading = computed(() => {
  return hospitalListLoading.value || patientListLoading.value || props.patientCountsLoading
})


let updateNowInterval = null

function updateNow() {
  updateNowInterval = setInterval(() => {
    now.value = new dayjs()
  }, 5000)
}


onMounted(() => {
  updateNow()
})

onBeforeUnmount(() => {
  if (updateNowInterval) {
    clearInterval(updateNowInterval)
  }
})

function showActionsModal(patient) {
  modals.value.showActionsModal(patient)
}

</script>

<template>
  <v-container fluid class="pa-0">
    <v-row dense>
      <v-col cols="12" md="auto">
        <QrScanButton @scan="query = $event.tic"/>
      </v-col>
      <v-col>
        <!-- SEARCH BAR -->
        <v-text-field clearable
                      variant="outlined"
                      :hide-spin-buttons="true"
                      hide-details
                      :loading="patientListLoading"
                      :label="pgettext('title', 'Patient search')"
                      :placeholder="pgettext('label', 'Put in TIC Card number / name / birth year / temporary identifier / birth number / note')"
                      v-model="query"
        ></v-text-field>
      </v-col>
    </v-row>
    <v-row dense>
      <v-col cols="12">
        <v-card>
          <v-card-title>
            <h2 class="text-center">{{ pgettext('label', 'Patients in Hospital') }}</h2>
          </v-card-title>
          <v-card-text>
            <EmergencyPatientList :patientList="filteredPatientList"
                                  :hpo="hpo"
                                  :member="member"
                                  :anyLoading="anyLoading"
                                  :hospitalList="hospitalList"
                                  :now="now"
                                  @display-actions-modal="showActionsModal"/>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>

  <DashboardModalsContainer ref="modals"
                            :countries="countries"
                            :member="member"
                            :hpo="hpo"
                            :patientList="patientList"/>
</template>